import ReactDOM from 'react-dom'
import TreeSelectRouter from './TreeSelectRouter'
import { BootstrapProps, Id } from './types'

const parseMaybeJson = <T,>(raw: string | null | undefined): T | null => {
	if (!raw) return null
	try {
		return JSON.parse(raw) as T
	} catch {
		return null
	}
}

const bootstrapOne = (el: HTMLElement) => {
	const endpoint = el.getAttribute('data-endpoint') ?? ''
	const titleKey = el.getAttribute('data-title-key') ?? 'title'
	const valueKey = el.getAttribute('data-value-key') ?? 'id'
	const multiple = el.getAttribute('data-multiple') === '1'
	const selectParents = el.getAttribute('data-select-parents') === '1'
	const isSearchable = el.getAttribute('data-is-searchable') === '1'
	const placeholder = el.getAttribute('data-placeholder')
	const inputId = el.getAttribute('data-input-id') ?? ''

	// Parse new sentiment and auto-open attributes
	const autoOpen = el.getAttribute('data-auto-open') === '1'
	const withSentiment = el.getAttribute('data-with-sentiment') === '1'
	const questionIdRaw = el.getAttribute('data-question-id')
	const questionId = questionIdRaw && questionIdRaw !== 'null' ? parseInt(questionIdRaw, 10) : null
	const initialSentimentsRaw = el.getAttribute('data-initial-sentiments')

	const defaultValueRaw = el.getAttribute('data-default-value')
	const defaultTitleRaw = el.getAttribute('data-default-title')
	const preloadedRaw = el.getAttribute('data-preloaded-data')

	let defaultValue: Id | Array<Id> | null = null
	let defaultTitle: string | Array<string> | null = null
	let preloadedData: Record<string, Array<Record<string, unknown>>> | null = null
	let preloadedActive: Array<Id> | null = null

	// defaultValue can be a primitive or a JSON array
	const parsedValues = parseMaybeJson<Array<Id>>(defaultValueRaw)
	if (parsedValues) {
		defaultValue = parsedValues
	} else if (defaultValueRaw !== null && defaultValueRaw !== '') {
		defaultValue = /^(\d+)$/.test(defaultValueRaw) ? Number(defaultValueRaw) : defaultValueRaw
	}

	// defaultTitle can be a string, an array, or null (JSON encoded)
	const parsedTitlesAny = parseMaybeJson<unknown>(defaultTitleRaw)
	if (Array.isArray(parsedTitlesAny)) {
		defaultTitle = parsedTitlesAny as Array<string>
	} else if (typeof parsedTitlesAny === 'string') {
		defaultTitle = parsedTitlesAny as string
	} else if (parsedTitlesAny === null) {
		defaultTitle = null
	} else if (defaultTitleRaw !== null && defaultTitleRaw !== '' && defaultTitleRaw !== 'null') {
		// fallback for plain string attributes not encoded as JSON
		defaultTitle = defaultTitleRaw
	}

	// Parse optional preloaded dataset: { data: Record<string, array>, active: array }
	const parsedPreloaded = parseMaybeJson<{ data?: Record<string, Array<Record<string, unknown>>>; active?: Array<Id> }>(
		preloadedRaw,
	)
	if (parsedPreloaded) {
		preloadedData = parsedPreloaded.data ?? null
		preloadedActive = parsedPreloaded.active ?? null
	}

	const props: BootstrapProps = {
		endpoint,
		titleKey,
		valueKey,
		multiple,
		selectParents,
		isSearchable,
		defaultValue,
		defaultTitle,
		placeholder: placeholder ?? null,
		inputId,
		preloadedData,
		preloadedActive,
		autoOpen,
		withSentiment,
		questionId,
		initialSentiments: parseMaybeJson<Record<string, Sentiment>>(initialSentimentsRaw),
	}

	ReactDOM.render(<TreeSelectRouter {...props} />, el)
}

export const bootstrapTreeSelect = (scope?: HTMLElement | Document) => {
	const root = scope ?? document
	const elements: NodeListOf<HTMLElement> = root.querySelectorAll('.js-tree-select')
	elements.forEach((el) => bootstrapOne(el))
}

// Auto-bootstrap on DOMContentLoaded
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => bootstrapTreeSelect())
} else {
	bootstrapTreeSelect()
}
